import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, IndianRupee, HelpCircle, Lock, TrendingUp, TrendingDown, Loader2, CheckCircle2, AlertTriangle, RefreshCw, ShieldCheck, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useGoldRate } from '../context/GoldRateContext';
import { useAuth } from '../context/AuthContext';
import { useProfileDrawer } from '../context/ProfileDrawerContext';
import api from '../services/api';

// Rotating tips for Refund Gold page
const refundGoldTips = [
  "Tip: Check the 7-day trend before refunding — patience can pay off",
  "Tip: Refunds are processed within 24-48 hours to your bank account",
  "Tip: Keep some Milli's saved for future gold price appreciation",
  "Tip: No lock-in period — refund anytime you need funds",
  "Tip: Partial refunds allowed — sell only what you need",
  "Tip: Compare our refund rate with local jewellers — we offer competitive rates",
  "Tip: No paperwork needed — instant digital refund process",
  "Tip: Consider redeeming as jewellery for special occasions instead",
  "Tip: Refund rate is based on live market — rates change throughout the day",
];

const RefundGoldPageNew = () => {
  const navigate = useNavigate();
  const { openDrawer } = useProfileDrawer();
  const { isAuthenticated, loading: authLoading, user, refreshUser } = useAuth();
  const { sellRate, sellRate22kt, loading: rateLoading } = useGoldRate();
  
  const [millis, setMillis] = useState(10);
  const [customMillis, setCustomMillis] = useState('10');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [refundResult, setRefundResult] = useState(null);
  
  // Random tip on page visit
  const [currentTip] = useState(() => refundGoldTips[Math.floor(Math.random() * refundGoldTips.length)]);
  
  // Real data states
  const [priceHistory, setPriceHistory] = useState([]);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(true);

  // Redirect unauthenticated users to home-new silently
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/home-new', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch 7-day sell rate history
  useEffect(() => {
    const fetchPriceHistory = async () => {
      try {
        const response = await api.get('/gold-rate/history?days=7&interval=daily');
        const data = response.data;
        
        if (data.history && data.history.length > 0) {
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          const chartData = data.history.map((item, index) => {
            const date = new Date(item.timestamp);
            const isToday = index === data.history.length - 1;
            return {
              day: isToday ? 'Today' : dayNames[date.getDay()],
              price: item.sell_rate || (item.rate_22k / 1000) * 0.98, // Use sell_rate
              date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
            };
          });
          setPriceHistory(chartData);
        }
      } catch (error) {
        console.error('Failed to fetch price history:', error);
        if (sellRate) {
          setPriceHistory([{ day: 'Today', price: sellRate }]);
        }
      } finally {
        setPriceHistoryLoading(false);
      }
    };

    fetchPriceHistory();
  }, [sellRate]);

  // Show nothing while checking auth (prevents flash of content)
  if (authLoading || !isAuthenticated) {
    return null;
  }

  const presetMillis = [50, 100, 500, 1000];
  const MIN_REFUND_MILLIS = 50;
  const userMilliBalance = user?.milli_balance || 0;
  const amountYouGet = millis * sellRate;
  const isBelowMinimum = millis < MIN_REFUND_MILLIS && millis > 0;
  
  // Calculate chart stats
  const minPrice = priceHistory.length > 0 ? Math.min(...priceHistory.map(p => p.price)) : 0;
  const maxPrice = priceHistory.length > 0 ? Math.max(...priceHistory.map(p => p.price)) : 0;
  const priceRange = maxPrice - minPrice || 1;
  const weekChange = priceHistory.length >= 2 
    ? ((priceHistory[priceHistory.length - 1].price - priceHistory[0].price) / priceHistory[0].price * 100).toFixed(2)
    : '0.00';
  const isPositive = parseFloat(weekChange) >= 0;

  // Check if user can refund - use kyc_verified flag or aadhaar_last_4 presence instead of full aadhaar
  const hasKYC = !!user?.kyc_verified || !!user?.aadhaar_last_4;
  const hasBankDetails = !!user?.bank_account_last4 || !!user?.bank_account_number;
  const canRefund = hasKYC && hasBankDetails && millis > 0 && millis <= userMilliBalance;

  const handleMillisChange = (value) => {
    const numValue = parseFloat(value) || 0;
    setMillis(numValue);
    setCustomMillis(value);
  };

  const handlePresetClick = (preset) => {
    setMillis(preset);
    setCustomMillis(preset.toString());
  };

  const handleContinue = () => {
    // Validate before showing confirmation
    if (!hasKYC) {
      toast.error('KYC verification required. Please complete Aadhaar verification first.');
      navigate('/kyc-new');
      return;
    }
    if (!hasBankDetails) {
      toast.error('Bank details required. Please add your bank account from your profile.');
      openDrawer();
      return;
    }
    if (millis <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (millis > userMilliBalance) {
      toast.error('Insufficient Milli balance');
      return;
    }
    if (millis < 1) {
      toast.error('Minimum 1 Milli required for refund');
      return;
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmRefund = async () => {
    setIsLoading(true);
    setShowConfirmModal(false);
    
    try {
      const response = await api.post('/milli/sell', {
        milli_amount: millis
      });
      
      setRefundResult(response.data);
      toast.success('Refund request submitted successfully!');
      
      // Refresh user data to update balance
      if (refreshUser) {
        await refreshUser();
      }
      
    } catch (error) {
      console.error('Refund error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to process refund. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewRefund = () => {
    setRefundResult(null);
    setMillis(10);
    setCustomMillis('10');
  };

  return (
    <>
      {/* Success Result Screen */}
      {refundResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Refund Submitted!</h3>
              <p className="text-gray-600 mb-4">
                Your refund request has been processed successfully.
              </p>
              
              <div className="bg-green-50 rounded-xl p-4 mb-4 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Milli&apos;s Sold</span>
                  <span className="font-semibold">{refundResult.milli_sold}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-bold text-green-600">₹{refundResult.amount_inr?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Rate Applied</span>
                  <span className="font-medium">₹{refundResult.rate?.toFixed(2)}/Milli</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Bank Account</span>
                  <span className="font-medium">{refundResult.bank_account}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">New Balance</span>
                  <span className="font-semibold">{refundResult.new_balance?.toFixed(2)} Milli&apos;s</span>
                </div>
              </div>
              
              <p className="text-sm text-amber-600 mb-4">
                Amount will be credited to your bank account within 24-48 hours.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/transaction-history')}
                  className="flex-1 py-3 bg-[#F5E6C8] hover:bg-[#EDD9B5] text-[#8B6914] font-medium rounded-xl"
                >
                  View History
                </button>
                <button
                  onClick={handleNewRefund}
                  className="flex-1 py-3 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white font-semibold rounded-xl"
                >
                  New Refund
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="text-center mb-4">
              <div className="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Confirm Refund</h3>
            </div>
            
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Milli&apos;s to Sell</span>
                <span className="font-semibold">{millis} Milli&apos;s</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Current Rate</span>
                <span className="font-medium">₹{sellRate?.toFixed(2)}/Milli</span>
              </div>
              <div className="flex justify-between border-t border-amber-200 pt-2 mt-2">
                <span className="text-gray-700 font-medium">You Will Receive</span>
                <span className="font-bold text-green-600">₹{amountYouGet.toFixed(2)}</span>
              </div>
            </div>
            
            <p className="text-sm text-gray-500 mb-4 text-center">
              Amount will be credited to your bank account ending in ****{user?.bank_account_last4 || user?.bank_account_number?.slice(-4) || '****'}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRefund}
                className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl"
              >
                Confirm Refund
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 min-h-[calc(100vh-80px)] flex flex-col">
        <div className="flex-1 flex flex-col">
          {/* Hero Section - Similar to Home Page alignment */}
          <div className="mb-6 sm:mb-8">
            {/* Title Section */}
            <div className="mt-2 sm:mt-4 lg:mt-6 mb-6">
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal leading-tight tracking-wide text-[#B8860B] mb-2 sm:mb-3" 
                style={{ fontFamily: 'Georgia, serif' }}
              >
                Refund Millis
              </h1>
              <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-xl">
                Get a refund on your Millis advance anytime, with the best value.
              </p>
            </div>
            
            {/* Stats Row - Live Refund Rate + 7-Day Trend */}
            <div className="flex flex-wrap gap-3 sm:gap-4">
              {/* Live Refund Rate Card */}
              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-[#004D40]/20 shadow-sm min-w-[180px]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#004D40]/10 border border-[#004D40]/30 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#004D40]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-[#004D40] uppercase tracking-wide">Live Refund Rate</p>
                    <p className="text-xl font-bold text-gray-900">
                      ₹{sellRate.toFixed(2)} <span className="text-xs font-normal text-gray-500">/ Milli</span>
                    </p>
                    <div className="border-t border-gray-200 mt-1 pt-1">
                      <p className="text-[10px] text-gray-500">22KT: ₹{sellRate22kt.toLocaleString('en-IN')} / g</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 7-Day Refund Rate Trend Card */}
              <div className="bg-white/50 backdrop-blur-sm rounded-xl p-4 border border-[#004D40]/20 shadow-sm min-w-[200px]">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] font-semibold text-[#004D40] uppercase tracking-wide">7-Day Refund Trend</p>
                  {!priceHistoryLoading && priceHistory.length > 1 && (
                    <div className={`flex items-center gap-1 text-[10px] font-medium ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                      {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      <span>{isPositive ? '+' : ''}{weekChange}%</span>
                    </div>
                  )}
                </div>
                
                {/* Sparkline */}
                {priceHistoryLoading ? (
                  <div className="h-8 flex items-center justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-[#004D40]" />
                  </div>
                ) : priceHistory.length > 1 ? (
                  <div className="relative h-8">
                    <svg className="w-full h-full" viewBox="0 0 160 30" preserveAspectRatio="none">
                      <path
                        d={`M 0 ${25 - ((priceHistory[0]?.price - minPrice) / priceRange) * 20} 
                            ${priceHistory.map((p, i) => `L ${i * (160 / (priceHistory.length - 1 || 1))} ${25 - ((p.price - minPrice) / priceRange) * 20}`).join(' ')}`}
                        fill="none"
                        stroke="#004D40"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx={160}
                        cy={25 - ((priceHistory[priceHistory.length - 1]?.price - minPrice) / priceRange) * 20}
                        r="3"
                        fill="#004D40"
                      />
                    </svg>
                  </div>
                ) : (
                  <div className="h-8 flex items-center text-xs text-gray-500">No data</div>
                )}
              </div>
            </div>
          </div>

          {/* Main Grid - Input, Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6 max-w-4xl">
            {/* Left Column - Enter Millis + Mini Balance Card */}
            <div className="space-y-4">
              {/* Enter Milli's Card */}
              <div className="bg-white/90 rounded-2xl p-5 shadow-sm border border-amber-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Enter Milli&apos;s to Refund</h2>
                
                {/* Milli's Input */}
                <div className="relative mb-4">
                  <input
                    type="text"
                    value={customMillis}
                    onChange={(e) => handleMillisChange(e.target.value)}
                    className="w-full px-4 py-4 text-2xl font-semibold text-gray-900 border border-amber-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B8860B] focus:border-transparent pr-20 bg-white"
                    placeholder="Enter milli's"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-lg text-gray-500">Milli&apos;s</span>
                </div>

                {/* Preset Amounts - Gold buttons - 4 options */}
                <div className="grid grid-cols-4 gap-2 mb-5">
                  {presetMillis.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => handlePresetClick(preset)}
                      disabled={preset > userMilliBalance}
                      className={`py-2 sm:py-2.5 rounded-lg font-medium text-xs sm:text-sm transition-all ${
                        millis === preset
                          ? 'bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white shadow-md'
                          : preset > userMilliBalance
                            ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                            : 'bg-[#F5E6C8] text-[#8B6914] border border-[#B8860B]/30 hover:bg-[#EDD9B5]'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>

                {/* Proceed to Refund Button */}
                <button
                  onClick={handleContinue}
                  disabled={isLoading || millis < MIN_REFUND_MILLIS || millis > userMilliBalance || rateLoading}
                  className="w-full py-4 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl ring-2 ring-[#B8860B]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Proceed to Refund'
                  )}
                </button>
                
                {/* Security Note */}
                <div className="flex items-center justify-center gap-2 mt-3 text-gray-600">
                  <Lock className="w-4 h-4" />
                  <p className="text-sm font-medium">All transactions are secure and encrypted.</p>
                </div>
                
                {/* Validation Messages */}
                {isBelowMinimum && (
                  <p className="text-sm text-amber-600 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Minimum {MIN_REFUND_MILLIS} Milli&apos;s required for refund
                  </p>
                )}
                {!hasKYC && (
                  <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    KYC verification required for refund
                  </p>
                )}
                {hasKYC && !hasBankDetails && (
                  <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Bank details required for refund
                  </p>
                )}
              </div>

              {/* Mini Milli Balance Card */}
              <div 
                className="rounded-xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.15),0_2px_8px_rgba(0,0,0,0.1)] relative overflow-hidden"
                style={{
                  backgroundImage: 'url(/assets-new/balance-card-bg.webp)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <div className="flex items-center justify-between relative z-10">
                  <div>
                    <p className="text-white/70 text-xs font-medium mb-1">Available to Refund</p>
                    <p className="text-2xl font-bold text-white">
                      {userMilliBalance.toFixed(2)} <span className="text-sm font-normal text-white/80">Milli&apos;s</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white/60 text-xs mb-1">Est. Value</p>
                    <p className="text-lg font-semibold text-[#FFD700]">
                      ₹{(userMilliBalance * sellRate).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          {/* Right Card - Refund Summary (Gold Theme) */}
          <div className="relative bg-[#F5E6C8] rounded-xl p-6 shadow-[inset_0_0_25px_rgba(139,90,43,0.12)] border-2 border-amber-400/60 flex flex-col">
            {/* Decorative corner accents */}
            <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-amber-600/70 rounded-tl-sm"></div>
            <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-amber-600/70 rounded-tr-sm"></div>
            <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-amber-600/70 rounded-bl-sm"></div>
            <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-amber-600/70 rounded-br-sm"></div>
            
            <h2 className="text-xl font-semibold text-amber-900 mb-6 text-center tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
              Refund Summary
            </h2>
            
            {/* Summary Items */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Milli&apos;s to Refund</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-20"></div>
                ) : (
                  <span className="font-semibold text-amber-900">{millis} Milli&apos;s</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Live Refund Rate (per Milli)</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-16"></div>
                ) : (
                  <span className="font-semibold text-amber-900">₹{sellRate.toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Amount You Will Get</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-24"></div>
                ) : (
                  <span className="font-bold text-green-700">₹{amountYouGet.toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between items-center py-2 border-b border-dashed border-amber-400/40">
                <span className="text-amber-800">Balance After Refund</span>
                {rateLoading ? (
                  <div className="animate-pulse h-5 bg-amber-300/40 rounded w-24"></div>
                ) : (
                  <span className="font-bold text-[#004D40]">{(userMilliBalance - millis).toFixed(2)} Milli&apos;s</span>
                )}
              </div>
            </div>

            {/* Processing Time */}
            <div className="flex items-center justify-center gap-2 text-amber-700/80 mb-3">
              <Clock className="w-4 h-4" />
              <p className="text-sm italic" style={{ fontFamily: 'Georgia, serif' }}>Processed within 24-48 hours.</p>
            </div>

            {/* Rotating Tip */}
            <div className="flex-1 flex items-end justify-center">
              <p className="text-xs text-amber-700/80 text-center italic px-2" style={{ fontFamily: 'Georgia, serif' }}>
                {currentTip}
              </p>
            </div>
          </div>
        </div>

        {/* Spacer to push banner to bottom */}
        <div className="flex-1"></div>

        {/* Trust Badges Row - Same background as Refund Summary card */}
        <div className="flex flex-col xl:flex-row gap-4 mt-6 mx-0">
          <div className="flex-1 bg-[#F5E6C8] rounded-xl p-4 border-2 border-amber-400/60 shadow-[inset_0_0_25px_rgba(139,90,43,0.12)] flex items-center justify-center">
            <div className="flex justify-center items-center gap-6 lg:gap-10 flex-wrap">
              {/* Trust Badge 1 - 100% Secure */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">100% Secure</p>
                  <p className="text-[10px] text-amber-700/70">Bank-grade encryption</p>
                </div>
              </div>

              {/* Trust Badge 2 - Fast Processing */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Fast Processing</p>
                  <p className="text-[10px] text-amber-700/70">24-48 hours</p>
                </div>
              </div>

              {/* Trust Badge 3 - Best Market Rate */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <IndianRupee className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Best Market Rate</p>
                  <p className="text-[10px] text-amber-700/70">Live rates</p>
                </div>
              </div>

              {/* Trust Badge 4 - Your Gold, Your Choice */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Your Gold, Your Choice</p>
                  <p className="text-[10px] text-amber-700/70">No lock-in period</p>
                </div>
              </div>

              {/* Trust Badge 5 - Hassle Free */}
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-amber-600/10 border border-amber-600/40 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-amber-800" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Hassle Free</p>
                  <p className="text-[10px] text-amber-700/70">No paperwork needed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default RefundGoldPageNew;
